import { PublicClientApplication, type AuthenticationResult } from '@azure/msal-browser';
import { msalInstance } from './msal';
import { authModule } from './auth-module';
import { isEnvFlagTrue, envList } from '@/config/env-flags';

/**
 * Multi-app-registration token acquisition for MFEs — port of the legacy
 * `enableSingleTenantAuth` path in
 * `raptor-shell-ui/.../raptor-msal.service.ts:acquireAccessTokenAcrossClientIds`.
 *
 * WHY: some MFEs (e.g. `notification-preferences`) request an access token for
 * a scope (`api://<app>/...`) that the shell's PRIMARY app registration
 * (`VITE_MSAL_CLIENT_ID`) has no consent for. The single-instance
 * `acquireTokenSilent` then fails / returns a token with the wrong audience and
 * the MFE's API call comes back 401. Legacy iterates the primary + a set of
 * ADDITIONAL app registrations and returns the first token that a silent
 * acquisition yields for the requested scope.
 *
 * This module is intentionally isolated from the primary auth flow (login,
 * route guards, shell `http()`): it only adds a silent fallback for the
 * `EY_RAPToR_getAccessTokenforMFE` global. When `VITE_MSAL_ADDITIONAL_CLIENT_IDS`
 * is empty the behaviour is identical to before (primary instance only).
 */
const PRIMARY_CLIENT_ID = import.meta.env.VITE_MSAL_CLIENT_ID;

const ADDITIONAL_CLIENT_IDS = envList(import.meta.env.VITE_MSAL_ADDITIONAL_CLIENT_IDS).filter(
  (id) => id !== PRIMARY_CLIENT_ID,
);

const additionalInstances = new Map<string, PublicClientApplication>();
let initPromise: Promise<void> | null = null;

function buildInstance(clientId: string): PublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId,
      authority: import.meta.env.VITE_MSAL_AUTHORITY,
      redirectUri: import.meta.env.VITE_MSAL_REDIRECT_URI,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: isEnvFlagTrue(import.meta.env.VITE_ENABLE_SESSION_STORAGE)
        ? 'sessionStorage'
        : 'localStorage',
      storeAuthStateInCookie: false,
    },
  });
}

/** Create + initialize the additional-clientId MSAL instances once. */
export function initAdditionalMsalInstances(): Promise<void> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    for (const clientId of ADDITIONAL_CLIENT_IDS) {
      try {
        const instance = buildInstance(clientId);
        await instance.initialize();
        additionalInstances.set(clientId, instance);
      } catch {
        /* a broken additional registration must not break the primary flow */
      }
    }
  })();
  return initPromise;
}

function primaryAccount() {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}

/**
 * Find the shared account inside an additional instance's cache. MSAL keys the
 * cache per clientId, but `homeAccountId` is stable across app registrations in
 * the same home tenant, so we look the primary account up by its home id.
 */
function resolveAccount(instance: PublicClientApplication) {
  const active = primaryAccount();
  if (!active) return instance.getAllAccounts()[0] ?? null;
  return instance.getAccountByHomeId(active.homeAccountId) ?? instance.getAllAccounts()[0] ?? null;
}

function extractToken(result: AuthenticationResult): string {
  return result.accessToken || result.idToken;
}

/**
 * Establish a session for an additional instance via SSO, leveraging the
 * existing AAD session cookie (no interaction). Returns the auth result when
 * scopes were supplied, else null after seeding the active account.
 */
async function trySsoSilent(
  instance: PublicClientApplication,
  scopes: string[],
): Promise<AuthenticationResult | null> {
  const active = primaryAccount();
  const loginHint = active?.username;
  if (!loginHint) return null;
  try {
    const result = await instance.ssoSilent({ loginHint, scopes });
    if (result.account) instance.setActiveAccount(result.account);
    return result;
  } catch {
    return null;
  }
}

/**
 * Warm up the additional instances after login so their caches hold the shared
 * account and the first MFE token call resolves silently. Best-effort.
 */
export async function warmUpAdditionalInstances(): Promise<void> {
  await initAdditionalMsalInstances();
  if (additionalInstances.size === 0) return;
  const active = primaryAccount();
  const loginHint = active?.username;
  if (!loginHint) return;
  await Promise.all(
    [...additionalInstances.values()].map(async (instance) => {
      if (resolveAccount(instance)) return;
      try {
        const result = await instance.ssoSilent({ loginHint });
        if (result.account) instance.setActiveAccount(result.account);
      } catch {
        /* best-effort — the on-demand path retries SSO when a token is needed */
      }
    }),
  );
}

function orderedAdditional(explicitClientId?: string): PublicClientApplication[] {
  const all = [...additionalInstances.entries()];
  if (!explicitClientId) return all.map(([, instance]) => instance);
  const targeted = additionalInstances.get(explicitClientId);
  const rest = all.filter(([id]) => id !== explicitClientId).map(([, instance]) => instance);
  return targeted ? [targeted, ...rest] : rest;
}

/**
 * Acquire an access token for `scopes`, trying the primary app registration
 * first (cached, single-flight, but never triggering an interactive redirect),
 * then each additional registration silently. Returns the first success.
 */
export async function acquireMfeTokenAcrossClientIds(
  scopes: string[] = [],
  explicitClientId?: string,
): Promise<string> {
  await initAdditionalMsalInstances();
  let lastError: unknown;

  if (!explicitClientId || explicitClientId === PRIMARY_CLIENT_ID) {
    try {
      return await authModule.getTokenAsync(scopes, { interactive: false });
    } catch (err) {
      lastError = err;
    }
  }

  for (const instance of orderedAdditional(explicitClientId)) {
    try {
      const account = resolveAccount(instance);
      if (!account) {
        const sso = await trySsoSilent(instance, scopes);
        if (sso) return extractToken(sso);
        continue;
      }
      const result = await instance.acquireTokenSilent({ scopes, account });
      return extractToken(result);
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError ?? new Error('Unable to acquire MFE token across client IDs');
}
